import { vesselIconCargoShip } from '../../../components/shared/vector-images';
import { callEndpoint, telemetry } from '../../backend';
import { definitions, subscribe } from '../../message-broker';
import { ensureBoatTopImages } from '../boat-icons/boat-icon-images';
import {
  BOAT_ICON_MIN_ZOOM,
  boatIconForAisIcon,
  boatIconLayout,
  normalizeHeading,
} from '../boat-icons/boat-icons';
import {
  registerBoatLayers,
  setBoatSourceData,
  tiltedBoatIconLayout,
  watchTiltedBoatView,
} from '../boat-icons/tilted-boats';
import {
  layerExists,
  layerVisible,
  sourceExists,
  toggleLayerIfRequired,
} from '../_helpers';

let mapReference;
let selectedMMSI = null;

// Tilted (3D) frames for a pitched map, on the same source
const tiltedLayerName = 'ais-tilted';

const getAisData = (boundingBox) => {
  // Use [minLon,minLat,maxLon,maxLat] format
  const convertBoundingBox = [
    boundingBox[0].lon,
    boundingBox[1].lat,
    boundingBox[1].lon,
    boundingBox[0].lat,
  ];
  telemetry(telemetry.DEBUG, `[queryAIS] ${convertBoundingBox}`);
  return callEndpoint(callEndpoint.endpoints.queryAis, {
    boundingBox: convertBoundingBox,
  });
};

const addLayersIfRequired = () => {
  if (!sourceExists(mapReference, 'ais')) {
    mapReference.addSource('ais', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }
  if (!layerExists(mapReference, 'ais')) {
    mapReference.addLayer({
      id: 'ais',
      type: 'symbol',
      source: 'ais',
      minzoom: BOAT_ICON_MIN_ZOOM,
      layout: boatIconLayout,
      paint: {},
    });
  }
  if (!layerExists(mapReference, tiltedLayerName)) {
    mapReference.addLayer({
      id: tiltedLayerName,
      type: 'symbol',
      source: 'ais',
      minzoom: BOAT_ICON_MIN_ZOOM,
      layout: tiltedBoatIconLayout,
      paint: {},
    });
    registerBoatLayers(mapReference, {
      sourceId: 'ais',
      topLayerId: 'ais',
      tiltedLayerId: tiltedLayerName,
    });
  }
};

const layer = {
  displayName: 'Vessel tracking',
  synonyms: '',
  group: 'tileLayer',
  typeUrl: 'vessel-tracking',
  icon: {
    baseShape: vesselIconCargoShip,
    iconColor: '#fff',
    markerColor: '#fff',
  },
  shownByDefault: true,
  relativeDepth: 10,
  menuOptions: {
    visible: true,
    owningMenu: 'vessel-tracking',
    order: 0,
  },
};

let requestDataTimeout;
let currentViewPort;
let lastQueryViewPort;

const aisDataCache = {};

const updateDataSource = async (map, fetch = true) => {
  if (!layerVisible(map, 'ais')) return;
  if (fetch || !aisDataCache.length) {
    const newData = await getAisData(currentViewPort.bounds);
    if (newData.results.length > 0) {
      newData.results.forEach((item) => {
        aisDataCache[`${item?.boatPayload?.mmsi}`] = item;
      });
    }
  }

  await setBoatSourceData(map, 'ais', {
    type: 'FeatureCollection',
    features: Object.entries(aisDataCache).map(([_, item]) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.longitude, item.latitude],
      },
      properties: {
        // Legacy SVG name, still used by the vessel details popover
        icon: item?.boatPayload?.icon,
        image: boatIconForAisIcon(item?.boatPayload?.icon),
        heading: normalizeHeading(item?.heading),
        mmsi: item?.boatPayload?.mmsi,
        type: item?.boatPayload?.type,
        shipName: item?.boatPayload?.shipName,
        speed: item?.speed,
        timestamp: item?.timestamp,
        markerSelected: item?.boatPayload?.mmsi === selectedMMSI,
      },
    })),
  });
};

const selectBoatMarker = async (map, mmsiToSelect) => {
  selectedMMSI = mmsiToSelect;
  await updateDataSource(map, false);
};

layer.onInitialize = (map) => {
  mapReference = map;
  addLayersIfRequired();

  ensureBoatTopImages(map).catch((error) => {
    telemetry(
      telemetry.WARN,
      `[ais mapLayer] Failed to load boat icons: ${error.message}`,
    );
  });

  watchTiltedBoatView(map);

  mapReference.on('deckee-press', ['ais', tiltedLayerName], async (event) => {
    if (!layerVisible(mapReference, 'ais')) return;
    // This is to select a marker and go to the popover
    const { mmsi } = event.features[0].properties;

    selectBoatMarker(mapReference, mmsi);
    // Prevent the map click event from propagating to other layers
    event.originalEvent.preventDefault();
  });
};

const updateData = async (force) => {
  // If we have yet to receive a reference to the map object through the onInitialize function,
  // just return as we can't update the data on the layer if the layer doesn't exist yet.
  if (!mapReference) return;
  // If not layer is not enabled to user don't update
  if (!layerVisible(mapReference, 'ais')) return;
  addLayersIfRequired();

  // If there isn't a view port to query for bounds, don't do anything.
  if (!currentViewPort) return;

  // If we're not forcing the update, and the bounds are the same as the
  // previous call, don't do anything
  if (!force && currentViewPort && currentViewPort.bounds === lastQueryViewPort)
    return;
  lastQueryViewPort = currentViewPort && currentViewPort.bounds;

  // Update the data for this layer every minute when the viewport doesn't change.
  if (requestDataTimeout) clearTimeout(requestDataTimeout);
  requestDataTimeout = setTimeout(() => updateData(true), 60 * 1000);

  await updateDataSource(mapReference, true);
};

layer.onToggle = (map, show) => {
  const currentlyVisible = layerVisible(map, 'ais');
  if (show && !currentlyVisible) {
    updateData();
  }
  toggleLayerIfRequired(map, 'ais', show);
  toggleLayerIfRequired(map, tiltedLayerName, show);
};

subscribe(definitions.map.viewPortUpdated, (viewPort) => {
  currentViewPort = viewPort;
  updateData();
});

export default layer;
